import { Button, Input } from '@ibsys/kit';
import { PlusIcon, Trash2Icon } from 'lucide-react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { CUSTOM_CHART_COLORS, type ChartValueColorRule, type CustomChartColor } from '@/entities/dashboards';
import { IconButton } from '@/shared/ui';
import { ChartColorPicker } from './ChartColorPicker';

type Props = {
  rules: ChartValueColorRule[];
  onChange: (rules: ChartValueColorRule[]) => void;
};

/** Цвет нового правила: первый цвет набора — им правило видно сразу, ещё до выбора своего. */
const DEFAULT_RULE_COLOR: CustomChartColor = CUSTOM_CHART_COLORS[0];

/** Введённая граница: пустое поле — границы нет, и с этой стороны правило не ограничено. */
const toBound = (value: string): number | undefined => {
  const trimmed = value.trim();
  if (trimmed === '') return undefined;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
};

/** Граница в поле ввода: незаданная показана пустым полем, а не нулём. */
const boundText = (value?: number): string => (value === undefined ? '' : String(value));

/**
 * Правила раскраски по величине: границы и цвет попавшего в них.
 *
 * Правила стоят перечнем в том порядке, в каком проверяются: величина красится первым подошедшим,
 * и порядок для пользователя — часть правила, а не оформление списка.
 *
 * Границы задаются с любой стороны: пустое поле границы не ставит, поэтому «больше 90» и «меньше
 * 50» набираются одним полем каждое.
 */
export const ChartValueColorRulesField: FC<Props> = ({ rules, onChange }) => {
  const { t } = useTranslation(['common', 'qualityDashboard']);

  const replaceRule = (index: number, rule: ChartValueColorRule) =>
    onChange(rules.map((current, position) => (position === index ? rule : current)));

  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm font-medium">{t('qualityDashboard:presets.editor.valueColorRules')}</p>
      <p className="text-xs text-muted-foreground">{t('qualityDashboard:presets.editor.valueColorRulesHint')}</p>

      {/* Ключ по месту в перечне: границы двух правил могут совпасть, а порядок — часть правила */}
      {rules.map((rule, index) => (
        <div key={index} className="flex flex-wrap items-end gap-3">
          <Input
            type="number"
            label={t('qualityDashboard:presets.editor.valueRuleFrom')}
            value={boundText(rule.from)}
            onChange={(event) => replaceRule(index, { ...rule, from: toBound(event.target.value) })}
            className="w-28"
          />
          <Input
            type="number"
            label={t('qualityDashboard:presets.editor.valueRuleTo')}
            value={boundText(rule.to)}
            onChange={(event) => replaceRule(index, { ...rule, to: toBound(event.target.value) })}
            className="w-28"
          />
          <ChartColorPicker
            value={rule.color}
            onChange={(color) => replaceRule(index, { ...rule, color: color ?? rule.color })}
          />
          <IconButton
            variant="ghost"
            size="icon"
            color="destructive"
            label={t('qualityDashboard:presets.editor.removeValueColorRule')}
            icon={<Trash2Icon className="size-4" />}
            onClick={() => onChange(rules.filter((_, position) => position !== index))}
          />
        </div>
      ))}

      <Button
        variant="secondary"
        size="sm"
        className="self-start"
        icon={<PlusIcon className="size-4" />}
        onClick={() => onChange([...rules, { color: DEFAULT_RULE_COLOR }])}
      >
        {t('qualityDashboard:presets.editor.addValueColorRule')}
      </Button>
    </div>
  );
};
